import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetContactQuery, useGetConversationQuery } from "@/store/rest_api";
import CommunicationItem from "@/model/interface/CommunicationItem";
import InboxFeed from "./InboxFeed";
import InboxThreadHeader from "./InboxThreadHeader";
import MessageThread from "./MessageThread";
import NewConversationDialog from "./NewConversationDialog";
import { Button } from "@/components/ui/button";
import { Archive, MessageSquarePlus } from "lucide-react";
import { useMessageDialog } from "@/context/MessageDialogContext";

const HIGHLIGHT_DURATION_MS = 3000;

export const InboxPage = () => {
  const { conversationId, contactId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const legacyConversationId = searchParams.get("conversation");
  const archivedView = searchParams.get("archived") === "1";
  const channelParam = searchParams.get("channel");
  const initialChannel =
    channelParam === "texts" || channelParam === "calls" || channelParam === "voicemail" ? channelParam : undefined;
  const [showArchived, setShowArchived] = useState(archivedView);
  const { data: linkedConversation } = useGetConversationQuery(conversationId ?? "", {
    skip: !conversationId,
  });
  const { data: threadContact } = useGetContactQuery(contactId ?? "", { skip: !contactId });
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const { setPendingRecipient, openMessageDialog } = useMessageDialog();

  const selected = useMemo(() => {
    if (!conversationId || linkedConversation?.id !== conversationId) {
      return null;
    }
    return linkedConversation;
  }, [conversationId, linkedConversation]);
  const contact = selected?.participants?.[0]?.contact ?? threadContact ?? null;
  const activeContactId = contact?.id ?? null;
  const hasThread = !!selected || !!threadContact;
  const { data: headerContact } = useGetContactQuery(activeContactId ?? "", { skip: !activeContactId });

  const leaveSelection = () => {
    navigate("/inbox", { replace: true });
  };

  const openItem =(item: CommunicationItem, searchResult: boolean) => {
    const suffix = new URLSearchParams();
    if (showArchived) {
      suffix.set("archived", "1");
    }
    if (searchResult) {
      suffix.set("item", item.id);
    }
    if (channelParam) {
      suffix.set("channel", channelParam);
    }
    const query = suffix.toString() ? `?${suffix.toString()}` : "";
    if (item.conversationId) {
      navigate(`/inbox/${item.conversationId}${query}`);
      return;
    }
    if (item.contact?.id) {
      navigate(`/inbox/contact/${item.contact.id}${query}`);
    }
  };

  const toggleArchived = () => {
    setShowArchived((value) => !value);
    leaveSelection();
  };

  const writeText = (phoneNumber: string) => {
    if (composerRef.current) {
      composerRef.current.focus();
      return;
    }
    setPendingRecipient(phoneNumber);
    openMessageDialog();
  };

  useEffect(() => {
    if (!legacyConversationId) {
      return;
    }
    navigate(`/inbox/${legacyConversationId}`, { replace: true });
  }, [legacyConversationId, navigate]);

  useEffect(() => {
    setHighlightedId(conversationId ?? null);
  }, [conversationId]);

  useEffect(() => {
    if (!highlightedId) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightedId(null), HIGHLIGHT_DURATION_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [highlightedId]);

  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-75 max-w-sm w-1/4 border-r border-border flex min-h-0 flex-col shrink-0">
        <div className="p-3 border-b border-border flex justify-between items-center">
          <span className="font-semibold text-sm">{showArchived ? "Archived" : "Inbox"}</span>
          <div className="flex items-center gap-1">
            <Button
              variant={showArchived ? "secondary" : "ghost"}
              size="icon"
              onClick={toggleArchived}
              title={showArchived ? "Back to the inbox" : "Archived conversations"}
            >
              <Archive className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setNewDialogOpen(true)} title="New conversation">
              <MessageSquarePlus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <InboxFeed
            archived={showArchived}
            initialChannel={initialChannel}
            selectedConversationId={selected?.id}
            selectedContactId={activeContactId ?? undefined}
            highlightedConversationId={highlightedId}
            onOpen={openItem}
          />
        </div>
      </div>
      <div className="flex flex-1 min-h-0 flex-col min-w-0">
        {hasThread && (
          <InboxThreadHeader
            contact={headerContact ?? contact}
            conversationPhoneNumber={selected?.participants?.[0]?.phoneNumber}
            conversationId={selected?.id}
            flagged={selected?.flagged}
            isGroup={selected?.isGroup ?? false}
            showsOptions={!showArchived && !selected?.deletedAt}
            onText={writeText}
            onArchived={leaveSelection}
          />
        )}
        {selected ? (
          <MessageThread
            conversation={selected}
            composerRef={composerRef}
            onRestored={() => setShowArchived(false)}
          />
        ) : threadContact ? (
          <MessageThread
            contact={threadContact}
            composerRef={composerRef}
            onRestored={() => setShowArchived(false)}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            {showArchived ? "Select an archived conversation" : "Select a conversation or start a new one"}
          </div>
        )}
      </div>

      <NewConversationDialog open={newDialogOpen} onOpenChange={setNewDialogOpen} />
    </div>
  );
};
